import { ChangeDetectionStrategy, Component, effect, input, output, signal } from '@angular/core';
import { QuizOption } from '@services/quiz-chat-service';

/** Stack = full-width vertical rows; Grid = 2-up, wrapping, odd count's last item spans full width. */
export enum QuizOptionListLayout { Stack = 'STACK', Grid = 'GRID' }

@Component({
  selector: 'app-quiz-option-list',
  templateUrl: './quiz-option-list.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuizOptionListComponent {
  readonly options = input.required<QuizOption[]>();
  readonly multiSelect = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly type = input<QuizOptionListLayout>(QuizOptionListLayout.Stack);
  /** Values already answered, so returning to a question by pressing back shows what was chosen. */
  readonly selectedValues = input<string | string[] | null>(null);
  readonly selectionChange = output<number[]>();

  readonly QuizOptionListLayout = QuizOptionListLayout;

  private readonly selectedIndices = signal<number[]>([]);

  constructor() {
    effect(() => {
      const options = this.options();
      const answered = this.selectedValues();
      const values = Array.isArray(answered) ? answered : [answered].filter(Boolean);
      this.selectedIndices.set(values
        .map((value) => options.findIndex((option) => option.value === value))
        .filter((index) => index >= 0));
    });
  }

  onOptionClick(index: number): void {
    if (this.disabled()) return;
    this.selectedIndices.set(this.computeNextSelection(index));
    this.selectionChange.emit(this.selectedIndices());
  }

  /** Keyboard activation for the div-as-button rows (Enter/Space). */
  onOptionKeydown(index: number, event: Event): void {
    event.preventDefault();
    this.onOptionClick(index);
  }

  isSelected(index: number): boolean {
    return this.selectedIndices().includes(index);
  }

  /** In grid layout an odd option count leaves the last row's single item spanning both columns. */
  isFullWidthItem(index: number): boolean {
    const count = this.options().length;
    return this.type() === QuizOptionListLayout.Grid && index === count - 1 && count % 2 === 1;
  }

  optionLabel(option: QuizOption): string {
    return option.text || option.value || '';
  }

  private computeNextSelection(index: number): number[] {
    if (!this.multiSelect()) return [index];
    const current = this.selectedIndices();
    if (current.includes(index)) return current.filter((selected) => selected !== index);
    const options = this.options();
    if (options[index]?.isExclusive) return [index];
    return [...current.filter((selected) => !options[selected]?.isExclusive), index];
  }
}
